import { useEffect, useRef, useState } from 'react';

export interface FilterOption {
  id: string;
  label: string;
  count?: number;
  icon?: string;
}

interface FilterSelectProps {
  value: string;
  options: FilterOption[];
  onChange: (value: string) => void;
  label?: string;
  className?: string;
}

export default function FilterSelect({
  value,
  options,
  onChange,
  label = 'Filter',
  className = '',
}: FilterSelectProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selected = options.find((option) => option.id === value) ?? options[0];

  useEffect(() => {
    if (!open) return undefined;
    const handlePointer = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="flex h-9 items-center gap-2 rounded-md border border-background-300 bg-background-50 pl-3 pr-2">
        <span className="shrink-0 text-xs font-label font-medium uppercase tracking-wider text-foreground-400">
          {label}
        </span>
        <span className="h-4 w-px shrink-0 bg-background-300" />
        <button
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          aria-haspopup="listbox"
          aria-expanded={open}
          className="flex h-full min-w-0 flex-1 cursor-pointer items-center gap-2 text-left"
        >
          {selected?.icon ? (
            <span className="flex h-4 w-4 shrink-0 items-center justify-center text-base text-foreground-500">
              <i className={selected.icon} />
            </span>
          ) : null}
          <span className="min-w-0 flex-1 truncate text-sm font-label font-medium text-foreground-800">
            {selected?.label ?? label}
          </span>
          {typeof selected?.count === 'number' ? (
            <span className="shrink-0 rounded-full bg-background-200 px-1.5 py-0.5 text-[11px] font-medium text-foreground-700">
              {selected.count}
            </span>
          ) : null}
          <i
            className={`ri-arrow-down-s-line shrink-0 text-foreground-500 transition-transform ${
              open ? 'rotate-180' : ''
            }`}
          />
        </button>
      </div>

      {open ? (
        <div
          role="listbox"
          className="absolute left-0 z-30 mt-1 w-full min-w-[220px] overflow-hidden rounded-md border border-background-300 bg-background-50"
        >
          <div className="max-h-72 overflow-y-auto py-1">
            {options.map((option) => {
              const active = option.id === value;
              return (
                <button
                  key={option.id}
                  type="button"
                  role="option"
                  aria-selected={active}
                  onClick={() => {
                    onChange(option.id);
                    setOpen(false);
                  }}
                  className={`flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-background-100 ${
                    active ? 'bg-background-100' : ''
                  }`}
                >
                  {option.icon ? (
                    <span className="flex h-4 w-4 shrink-0 items-center justify-center text-base text-foreground-500">
                      <i className={option.icon} />
                    </span>
                  ) : null}
                  <span
                    className={`min-w-0 flex-1 truncate ${
                      active ? 'font-medium text-foreground-950' : 'text-foreground-800'
                    }`}
                  >
                    {option.label}
                  </span>
                  {typeof option.count === 'number' ? (
                    <span className="shrink-0 text-[11px] font-label text-foreground-400">
                      {option.count}
                    </span>
                  ) : null}
                  <i
                    className={`shrink-0 text-base text-primary-600 ${
                      active ? 'ri-check-line' : 'opacity-0'
                    }`}
                  />
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}